/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  State
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
/* CUSTOM IMPORTS */
import { Locale } from "../../../common/locale";
import { config } from "../../../common/config";

@Component({
  tag: "gx-ide-bpm-assign-roles",
  styleUrl: "bpm-assign-roles.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-assign-roles"]
})
export class GxIdeBpmAssignRoles {
  // 1.OWN PROPERTIES //

  /**
   * The component hard-coded strings translations.
   */
  private _componentLocale: any;
  private renderedFirstTime = false;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeBpmAssignRolesElement;
  private unassignedRolesEL: HTMLGxgListBoxElement;
  private assignedRolesEl: HTMLGxgListBoxElement;

  // 3.STATE() VARIABLES //

  @State() unassignedRolesState: string[] = [];
  @State() assignedRolesState: string[] = [];
  @State() filterUnassignedInput = "";
  @State() filterAssignedInput = "";
  @State() unassignedSelected = false;
  @State() assignedSelected = false;

  // 4.PUBLIC PROPERTY API | WATCH'S //

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   * Array with all available roles
   */
  @Prop() readonly roles: string[];

  /**
   * Array with currently assigned roles
   */
  @Prop() readonly assignedRoles: string[];

  /**
   * Callback invoked when user confirms assignation
   */
  @Prop() readonly confirmCallback: ConfirmCallback;

  /**
   * Callback invoked when user cancels assignation
   */
  @Prop() readonly cancelCallback: CancelCallback;

  // 5.EVENTS (EMIT) //

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  // 6.COMPONENT LIFECYCLE METHODS //

  async componentWillLoad() {
    this._componentLocale = await Locale.getComponentStrings(this.el);
    this.unassignedRolesState = this.roles.filter(
      role => !this.assignedRoles.includes(role)
    );
    this.assignedRolesState = this.assignedRoles;
  }

  componentDidRender() {
    if (!this.renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this._componentLocale.componentName
      );
      this.renderedFirstTime = true;
    }
  }

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  private confirmCallbackHandler = () => {
    this.confirmCallback(this.assignedRolesState);
  };

  private cancelCallbackHandler = () => {
    this.cancelCallback();
  };

  private handleAssignedCheckedChange = (e: any) => {
    this.assignedSelected = e.detail[0] && e.detail[0].checked === true;
  };

  private handleUnassignedCheckedChange = (e: any) => {
    this.unassignedSelected = e.detail[0] && e.detail[0].checked === true;
  };

  private handleUnassignedInput = (e: any) => {
    this.filterUnassignedInput = e.detail;
  };

  private handleAssignedInput = (e: any) => {
    this.filterAssignedInput = e.detail;
  };
  private getCheckedItems = (listBox: any) => {
    const checkedItems: string[] = [];
    const listBoxItems = listBox.querySelectorAll("gxg-list-box-item");

    listBoxItems.forEach((item: any) => {
      if (item.checked) {
        checkedItems.push(item.value);
      }
    });

    return checkedItems;
  };

  private addAll = () => {
    this.assignedRolesState = [
      ...this.assignedRolesState,
      ...this.unassignedRolesState
    ];
    this.unassignedRolesState = [];
  };

  private addSelectedRoles = () => {
    const checkedItems = this.getCheckedItems(this.unassignedRolesEL);
    if (checkedItems.length) {
      this.unassignedRolesState = this.unassignedRolesState.filter(
        role => !checkedItems.includes(role)
      );
      this.assignedRolesState = [...this.assignedRolesState, ...checkedItems];
      this.unassignedSelected = false;
    }
  };

  private removeAll = () => {
    this.unassignedRolesState = [
      ...this.unassignedRolesState,
      ...this.assignedRolesState
    ];
    this.assignedRolesState = [];
  };

  private removeSelectedRoles = () => {
    const checkedItems = this.getCheckedItems(this.assignedRolesEl);

    if (checkedItems.length) {
      this.assignedRolesState = this.assignedRolesState.filter(role => {
        return !checkedItems.includes(role);
      });
      this.unassignedRolesState = [
        ...this.unassignedRolesState,
        ...checkedItems
      ];
      this.assignedSelected = false;
    }
  };

  private renderRolesToAdd = () => {
    return (
      <div class="roles__list">
        <gxg-list-box
          checkboxes
          disabled={false}
          part="add-roles-selection"
          onCheckedChanged={this.handleUnassignedCheckedChange}
          ref={(el: HTMLGxgListBoxElement) =>
            (this.unassignedRolesEL = el as HTMLGxgListBoxElement)
          }
          noBorder
          borderBottom
          borderStart
          borderEnd
        >
          {this.unassignedRolesState
            .filter(
              role =>
                this.filterUnassignedInput.toLowerCase() === "" ||
                role
                  .toLowerCase()
                  .includes(this.filterUnassignedInput.toLowerCase())
            )
            .map(role => (
              <gxg-list-box-item
                value={role}
                checkbox
                key={`${role}-${
                  this.assignedRolesState.includes(role)
                    ? "assigned"
                    : "unassigned"
                }`}
              >
                {role}
              </gxg-list-box-item>
            ))}
        </gxg-list-box>
      </div>
    );
  };
  private renderRolesToRemove = () => {
    return (
      <div class="roles__list">
        <gxg-list-box
          checkboxes
          disabled={false}
          part="remove-roles-selection"
          onCheckedChanged={this.handleAssignedCheckedChange}
          ref={(el: HTMLGxgListBoxElement) =>
            (this.assignedRolesEl = el as HTMLGxgListBoxElement)
          }
          noBorder
          borderBottom
          borderStart
          borderEnd
        >
          {this.assignedRolesState
            .filter(
              role =>
                this.filterAssignedInput.toLowerCase() === "" ||
                role
                  .toLowerCase()
                  .includes(this.filterAssignedInput.toLowerCase())
            )
            .map(role => (
              <gxg-list-box-item
                value={role}
                checkbox
                key={`${role}-${
                  this.assignedRolesState.includes(role)
                    ? "assigned"
                    : "unassigned"
                }`}
              >
                {role}
              </gxg-list-box-item>
            ))}
        </gxg-list-box>
      </div>
    );
  };

  // 10.RENDER() FUNCTION //

  render() {
    return (
      <Host class="gx-ide-component">
        <div class="gx-ide-main-wrapper">
          <gx-ide-container
            noAboveFooterPadding
            slimmerFooter={config.gxIdeContainer.slimmerFooter}
          >
            <div class="roles__container">
              {/* add roles section */}
              <section class="roles__role">
                {/* roles to add listbox*/}
                <header class="roles__list-header">
                  {/* roles to add filter*/}
                  <gxg-form-text
                    label={this._componentLocale.main.assignRoles.searchRoles}
                    placeholder={
                      this._componentLocale.main.assignRoles.placeholder
                    }
                    labelPosition="above"
                    clearButton
                    part="add-roles-filter"
                    class={{ "add-roles-filter": true }}
                    onInput={this.handleUnassignedInput}
                  ></gxg-form-text>
                  <div class="roles__list-title">
                    <gxg-title type="title-04">
                      {this._componentLocale.main.assignRoles.title}
                    </gxg-title>
                    <gxg-icon
                      type="navigation/chevron-right"
                      color="mercury-neutral"
                    ></gxg-icon>
                  </div>
                </header>
                {this.renderRolesToAdd()}
              </section>

              {/* remove roles section */}
              <section class="roles__role">
                <header class="roles__list-header">
                  {/* roles to remove filter*/}
                  <gxg-form-text
                    label={this._componentLocale.main.removeRoles.searchRoles}
                    placeholder={
                      this._componentLocale.main.removeRoles.placeholder
                    }
                    labelPosition="above"
                    clearButton
                    part="remove-roles-filter"
                    class={{ "remove-roles-filter": true }}
                    onInput={this.handleAssignedInput}
                  ></gxg-form-text>
                  <div class="roles__list-title">
                    <gxg-icon
                      type="navigation/chevron-left"
                      color="mercury-neutral"
                    ></gxg-icon>
                    <gxg-title type="title-04">
                      {this._componentLocale.main.removeRoles.title}
                    </gxg-title>
                  </div>
                </header>
                {/* roles to remove listbox*/}
                {this.renderRolesToRemove()}
              </section>
            </div>

            <footer class="add-remove-buttons" slot="footer-above">
              <footer class="add-remove-buttons__add">
                <gxg-button
                  id="add-all"
                  part="add-all-roles-btn"
                  onClick={this.addAll}
                  disabled={this.unassignedRolesState.length === 0}
                  type="secondary-text-only"
                >
                  {this._componentLocale.main.assignRoles.addAllButton}
                </gxg-button>
                <gxg-button
                  id="add-selected"
                  part="add-selected-roles-btn"
                  onClick={this.addSelectedRoles}
                  disabled={
                    !this.unassignedSelected ||
                    !this.unassignedRolesState.length
                  }
                  type="secondary-text-only"
                >
                  {this._componentLocale.main.assignRoles.addSelectedButton}
                </gxg-button>
              </footer>
              <footer class="add-remove-buttons__remove">
                <gxg-button
                  id="add-all"
                  part="remove-all-roles-btn"
                  onClick={this.removeAll}
                  disabled={this.assignedRolesState.length === 0}
                  type="secondary-text-only"
                >
                  {this._componentLocale.main.removeRoles.removeAllButton}
                </gxg-button>
                <gxg-button
                  id="add-selected"
                  part="remove-selected-roles-btn"
                  onClick={this.removeSelectedRoles}
                  disabled={
                    !this.assignedSelected || !this.assignedRolesState.length
                  }
                  type="secondary-text-only"
                >
                  {this._componentLocale.main.removeRoles.removeSelectedButton}
                </gxg-button>
              </footer>
            </footer>

            {/* cancel button*/}
            <gxg-button
              type="outlined"
              slot="footer-end"
              onClick={this.cancelCallbackHandler}
            >
              {this._componentLocale.footer.cancelButton}
            </gxg-button>
            {/* apply button*/}
            <gxg-button slot="footer-end" onClick={this.confirmCallbackHandler}>
              {this._componentLocale.footer.confirmButton}
            </gxg-button>
          </gx-ide-container>
        </div>
      </Host>
    );
  }
}

export type ConfirmCallback = (assignedRoles: string[]) => Promise<void>;

export type CancelCallback = () => Promise<void>;
